import { useState } from 'react';
import { Link as RouterLink, useLocation, useNavigate } from 'react-router-dom';
import {
  Avatar, Box, Divider, IconButton, ListItemIcon, Menu, MenuItem, Stack, Tooltip, Typography,
} from '@mui/material';
import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import LogoutIcon from '@mui/icons-material/Logout';
import { piper } from '../../theme/theme';
import { roleLabels } from '../../config/menuItems';
import usePiperFeatures from '../../hooks/usePiperFeatures';
import { usePiperConsent } from './PiperConsentContext';

export const NAV_RAIL_WIDTH = 64;

/** Max icon items shown before overflowing into the "More" flyout. */
const MAX_RAIL_ITEMS = 6;

const RailItem = ({ item, active, onClick }) => (
  <Tooltip title={item.text} placement="right">
    <Box
      component={item.path ? RouterLink : 'button'}
      to={item.path}
      onClick={onClick}
      aria-current={active ? 'page' : undefined}
      sx={{
        width: 40,
        height: 40,
        borderRadius: '9px',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: '2px',
        cursor: 'pointer',
        textDecoration: 'none',
        border: 'none',
        color: active ? '#fff' : 'rgba(255,255,255,.55)',
        backgroundColor: active ? 'rgba(255,255,255,.14)' : 'transparent',
        transition: 'background-color .15s ease, color .15s ease',
        '&:hover': { color: '#fff', backgroundColor: 'rgba(255,255,255,.08)' },
        '&:focus-visible': { outline: '2px solid rgba(255,255,255,.6)', outlineOffset: 1 },
      }}
    >
      <item.icon sx={{ fontSize: 18 }} />
      <Typography component="span" sx={{ fontSize: '8px', fontWeight: 600, lineHeight: 1, letterSpacing: '.02em' }}>
        {item.railLabel || item.text}
      </Typography>
    </Box>
  </Tooltip>
);

/**
 * NavRail — fixed 64px dark-slate left rail: logo square, icon+label nav,
 * user avatar pinned to the bottom.
 */
const NavRail = ({ menuItems = [], brandLink = '/', currentUser, onLogout }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [moreAnchor, setMoreAnchor] = useState(null);
  const [userAnchor, setUserAnchor] = useState(null);
  const { declined, access } = usePiperFeatures();
  const { openTermsModal } = usePiperConsent();
  const showPiperReenable = declined && access?.tenantEnabled === true && access?.brokerEnabled === true;

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const visible = menuItems.slice(0, MAX_RAIL_ITEMS);
  const overflow = menuItems.slice(MAX_RAIL_ITEMS);

  return (
    <Box
      component="nav"
      aria-label="Primary"
      sx={{
        position: 'fixed',
        left: 0,
        top: 0,
        bottom: 0,
        width: NAV_RAIL_WIDTH,
        backgroundColor: piper.navRail,
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        py: 1.5,
        zIndex: (t) => t.zIndex.drawer + 1,
      }}
    >
      {/* Logo — white rounded square */}
      <Box
        component={RouterLink}
        to={brandLink}
        aria-label="Home"
        sx={{
          width: 34,
          height: 34,
          borderRadius: '8px',
          backgroundColor: '#fff',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          mb: 2,
          flexShrink: 0,
          overflow: 'hidden',
        }}
      >
        <Box component="img" src="/assets/ff-logo.png" alt="United Fidelity Funding Corp." sx={{ width: 28, height: 28, objectFit: 'contain' }} />
      </Box>

      <Stack spacing={0.75} alignItems="center" sx={{ flex: 1, overflow: 'hidden' }}>
        {visible.map((item) => (
          <RailItem key={item.id} item={item} active={isActive(item.path)} />
        ))}
        {overflow.length > 0 && (
          <>
            <RailItem
              item={{ text: 'More', railLabel: 'More', icon: MoreHorizIcon }}
              active={overflow.some((i) => isActive(i.path))}
              onClick={(e) => setMoreAnchor(e.currentTarget)}
            />
            <Menu
              anchorEl={moreAnchor}
              open={Boolean(moreAnchor)}
              onClose={() => setMoreAnchor(null)}
              anchorOrigin={{ vertical: 'center', horizontal: 'right' }}
              transformOrigin={{ vertical: 'center', horizontal: 'left' }}
            >
              {overflow.map((item) => (
                <MenuItem
                  key={item.id}
                  selected={isActive(item.path)}
                  onClick={() => { setMoreAnchor(null); navigate(item.path); }}
                >
                  <ListItemIcon><item.icon fontSize="small" /></ListItemIcon>
                  {item.text}
                </MenuItem>
              ))}
            </Menu>
          </>
        )}
      </Stack>

      {/* User avatar pinned to the bottom */}
      <Tooltip title={currentUser?.name || 'Account'} placement="right">
        <IconButton size="small" onClick={(e) => setUserAnchor(e.currentTarget)} aria-label="user menu">
          <Avatar
            src={currentUser?.avatar}
            alt={currentUser?.name}
            sx={{ width: 32, height: 32, fontSize: '0.8rem', backgroundColor: piper.slate, color: '#fff', fontWeight: 700 }}
          >
            {currentUser?.name?.split(' ').map((p) => p[0]).slice(0, 2).join('')}
          </Avatar>
        </IconButton>
      </Tooltip>
      <Menu
        anchorEl={userAnchor}
        open={Boolean(userAnchor)}
        onClose={() => setUserAnchor(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
        transformOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        disableScrollLock
      >
        <Box sx={{ px: 2, py: 1, minWidth: 190 }}>
          <Typography variant="body2" fontWeight={600} noWrap>{currentUser?.name}</Typography>
          <Typography variant="caption" color="text.secondary" noWrap display="block">
            {roleLabels[currentUser?.role] || currentUser?.role}
          </Typography>
        </Box>
        <Divider sx={{ my: 0.5 }} />
        {showPiperReenable && (
          <MenuItem onClick={() => { setUserAnchor(null); openTermsModal(); }}>
            <Typography variant="body2">Piper is disabled — review terms</Typography>
          </MenuItem>
        )}
        <MenuItem onClick={() => { setUserAnchor(null); onLogout?.(); }}>
          <ListItemIcon><LogoutIcon fontSize="small" /></ListItemIcon>
          <Typography variant="body2">Logout</Typography>
        </MenuItem>
      </Menu>
    </Box>
  );
};

export default NavRail;
